import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Detail, Heading} from '@forecast-it/design-system';
import {wrapLongText} from '../../../styles/wrapLongText';
import type {TicketDetailHeader_ticket$key} from './__generated__/TicketDetailHeader_ticket.graphql';
import {TicketStatusBadge} from './TicketStatusBadge';
import {PriorityBadge} from './PriorityBadge';
import {SLABadge} from './SLABadge';
import {TicketBreadcrumb} from './TicketBreadcrumb';
import {TicketActionBar} from './TicketActionBar';
import {useWatchTicket} from '../hooks/useWatchTicket';
import {formatCreated} from '../utils/formatCreated';
import {CHANNEL_LABELS} from '../constants/channels';
import type {TicketChannel, TicketPriority, TicketStatus} from '../types/ticket';

const TicketHeaderFragment = graphql`
	fragment TicketDetailHeader_ticket on Ticket {
		id
		subject
		description
		status
		priority
		channel
		slug
		number
		createdAt
		assigneeId
		isWatching
		watcherCount
		totalLoggedHours
		totalBillableHours
		sla {
			breached
			elapsedSeconds
			thresholdSeconds
			paused
		}
		requester {
			name
		}
	}
`;

interface Props {
	ticket: TicketDetailHeader_ticket$key;
	onResolve?: () => void;
	saving?: boolean;
	resolving?: boolean;
	/** Perm-gated by the route; threads through to the action bar's menu. */
	onDelete?: () => void;
}

const Header = styled.header`
	margin-bottom: ${p => p.theme.sys.spacing[400]};
`;
const CrumbRow = styled.div`
	margin-bottom: ${p => p.theme.sys.spacing[300]};
`;
const TopRow = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[500]};
`;
const Identity = styled.div`
	min-width: 0;
`;
// The state row: id, then the three readings that decide whether this ticket needs
// attention. Nothing here is a control — the Details panel owns the editing.
const BadgeRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const Subject = styled(Heading)`
	${wrapLongText}
	margin: ${p => p.theme.sys.spacing[200]} 0 0;
`;
const MetaRow = styled.div`
	margin-top: ${p => p.theme.sys.spacing[100]};
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;

function formatLoggedHours(hours: number): string {
	return Number(hours.toFixed(2)).toString();
}

/**
 * The detail page's identity block: where you are, what state the ticket is in,
 * what it is called, and where it came from — then the actions.
 *
 * The provenance line is one quiet sentence rather than a row of chips, so the
 * subject stays the loudest thing on the page. Logged hours join it only once
 * there are any.
 */
export function TicketDetailHeader({ticket, onResolve, saving, resolving, onDelete}: Props) {
	const data = useFragment<TicketDetailHeader_ticket$key>(TicketHeaderFragment, ticket);
	const {watch, unwatch, inFlight: watchInFlight} = useWatchTicket();

	const channelLabel = CHANNEL_LABELS[data.channel as TicketChannel] ?? data.channel;

	return (
		<Header>
			<CrumbRow>
				<TicketBreadcrumb slug={data.slug} />
			</CrumbRow>

			<TopRow>
				<Identity>
					<BadgeRow>
						<Detail color="medium">{data.slug}</Detail>
						<TicketStatusBadge status={data.status as TicketStatus} />
						<PriorityBadge priority={data.priority as TicketPriority} />
						<SLABadge ticket={{sla: data.sla}} compact />
					</BadgeRow>

					{/* forwardedAs, not as: on a styled(Heading), `as` replaces Heading itself and
					    drops its typography. forwardedAs hands the tag through to Heading. */}
					<Subject size="2" forwardedAs="h1">
						{data.subject}
					</Subject>

					<MetaRow>
						<Detail color="medium" data-testid="ticket-provenance">
							{data.requester?.name ? `Opened by ${data.requester.name} ` : 'Opened '}
							{formatCreated(data.createdAt)} · Channel: {channelLabel}
						</Detail>
						{data.totalLoggedHours > 0 && (
							<Detail color="medium" data-testid="ticket-logged-hours">
								{formatLoggedHours(data.totalLoggedHours)}h logged
								{data.totalBillableHours !== data.totalLoggedHours &&
									` (${formatLoggedHours(data.totalBillableHours)}h billable)`}
							</Detail>
						)}
					</MetaRow>
				</Identity>

				<TicketActionBar
					status={data.status}
					assigneeId={data.assigneeId ?? null}
					isWatching={data.isWatching}
					watchCount={data.watcherCount}
					onWatch={() => watch(data.id)}
					onUnwatch={() => unwatch(data.id)}
					watchInFlight={watchInFlight}
					onResolve={onResolve ?? (() => {})}
					saving={saving}
					resolving={resolving}
					onDelete={onDelete}
				/>
			</TopRow>
		</Header>
	);
}
